import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/landing/Navbar";
import Footer from "../components/landing/Footer";
import Seo, { ORGANIZATION_JSONLD } from "../components/Seo";
import {
  PAGE_SEO,
  PROBLEM_SELECTOR,
  SERVICES,
  HOW_AXIS_WORKS,
  WHY_AXIS_POINTS,
  TRUST_BAR,
  WA_LINKS,
} from "../data/siteData";
import { ArrowUpRight, ArrowRight } from "lucide-react";

const Eyebrow = ({ children, light = false }) => (
  <div className="flex items-center gap-3 mb-5">
    <span className={`h-px w-10 ${light ? "bg-[#D4AF37]" : "bg-[#C0C0C0]"}`} />
    <span className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">
      {children}
    </span>
  </div>
);

const PROOF_CARDS = [
  {
    id: "executive-grooming",
    img: "/assets/case-studies/eg-cover.png",
    client: "Executive Grooming",
    problem: "Premium barbershop with an experience that undersold the craft.",
    service: "Experience Blueprint™",
  },
  {
    id: "wig-doctor",
    img: "/assets/case-studies/wig-doctor-page-1.png",
    client: "The Wig Doctor",
    problem: "A specialty wig-care service that needed a memorable brand identity.",
    service: "Character Branding™",
  },
  {
    id: "authentic-loft",
    img: "/assets/case-studies/loft-cover.png",
    client: "The Authentic Loft",
    problem: "A feminine boutique needing a recognizable brand ambassador.",
    service: "Character Branding™",
  },
];

const ProblemSelectorSection = () => {
  const [activeProblem, setActiveProblem] = useState(PROBLEM_SELECTOR[0].id);
  const selected = PROBLEM_SELECTOR.find((p) => p.id === activeProblem);

  return (
    <section data-testid="home-problem-selector" className="bg-[#F8F9FA] py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="max-w-2xl mb-12">
              <Eyebrow>Start With the Problem</Eyebrow>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold text-[#0A192F] leading-[1.1]">
                What are you trying to improve?
              </h2>
              <p className="mt-6 text-sm md:text-base text-[#475569] leading-relaxed">
                You do not need to diagnose the solution yourself. Choose the challenge that
                sounds closest, and AXIS will recommend the right starting point.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-5 flex flex-col gap-2" role="tablist" aria-label="Business problems">
                {PROBLEM_SELECTOR.map((p) => {
                  const isActive = p.id === activeProblem;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      data-testid={`problem-${p.id}`}
                      onClick={() => setActiveProblem(p.id)}
                      className={`text-left px-5 py-4 border transition-all duration-200 ${
                        isActive
                          ? "bg-[#0A192F] text-white border-[#0A192F]"
                          : "bg-white text-[#0A192F] border-[#C0C0C0]/40 hover:border-[#D4AF37]"
                      }`}
                    >
                      <span className="text-sm md:text-base font-semibold">{p.problem}</span>
                    </button>
                  );
                })}
              </div>
              <div className="lg:col-span-7">
                <div
                  data-testid="problem-detail"
                  className="bg-white border border-[#C0C0C0]/40 p-8 md:p-10 h-full flex flex-col"
                >
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-3">
                    Recommended Service
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl font-semibold text-[#0A192F]">
                    {selected.service}
                  </h3>
                  <p className="mt-4 text-sm md:text-base text-[#475569] leading-relaxed flex-1">
                    {selected.blurb}
                  </p>
                  <div className="mt-8">
                    <Link
                      to={selected.to}
                      data-testid="problem-detail-cta"
                      className="inline-flex items-center justify-center gap-2 bg-[#0A192F] text-white px-6 py-3 text-sm font-semibold border border-[#0A192F] hover:bg-transparent hover:text-[#0A192F] transition-all duration-300"
                    >
                      {selected.cta} <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
  );
};

export default function Landing() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="landing-page" className="bg-white">
      <Seo {...PAGE_SEO.home} jsonLd={ORGANIZATION_JSONLD} />
      <Navbar />
      <main>
        {/* HERO */}
        <section
          data-testid="home-hero"
          className="relative bg-[#0A192F] text-white pt-32 pb-16 md:pt-44 md:pb-24 overflow-hidden"
        >
          <div
            className="absolute inset-0 opacity-25 pointer-events-none bg-cover bg-center"
            style={{
              backgroundImage:
                "url(https://images.unsplash.com/photo-1573164574572-cb89e39749b4?auto=format&fit=crop&w=1600&q=80)",
            }}
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F] via-[#0A192F]/85 to-[#0A192F]/40 pointer-events-none" aria-hidden="true" />
          <div className="relative max-w-7xl mx-auto px-6 md:px-12">
            <Eyebrow light>AXIS Business Solutions</Eyebrow>
            <h1
              data-testid="home-headline"
              className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-semibold leading-[1.02] max-w-4xl"
            >
              Make your business easier to <span className="text-[#D4AF37]">understand, trust, and remember.</span>
            </h1>
            <p className="mt-7 text-base md:text-lg text-[#C0C0C0] leading-relaxed max-w-2xl">
              AXIS helps growing Bahamian organizations launch professional digital
              experiences, create recognizable character brands, and
              improve the way customers experience the business.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                href={WA_LINKS.recommend}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-hero-cta-primary"
                className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A192F] px-7 py-3.5 text-sm md:text-base font-semibold border border-[#D4AF37] hover:bg-transparent hover:text-[#D4AF37] transition-all duration-300"
              >
                Get My AXIS Recommendation →
              </a>
              <Link
                to="/services"
                data-testid="home-hero-cta-secondary"
                className="inline-flex items-center justify-center gap-2 bg-transparent text-white border border-[#C0C0C0]/40 px-6 py-3 text-sm font-semibold hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300"
              >
                Explore Services
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-2">
              <span className="h-px w-8 bg-[#D4AF37]" />
              <span className="text-[11px] uppercase tracking-[0.25em] text-[#94A3B8] font-semibold">
                AI Optimization. Human Judgment.
              </span>
            </div>
          </div>
        </section>

        {/* TRUST BAR */}
        <section data-testid="home-trust-bar" className="bg-[#0A192F] border-t border-[#C0C0C0]/15">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            {TRUST_BAR.map((t) => (
              <div key={t} className="flex items-center gap-2.5 text-xs md:text-sm text-[#C0C0C0]">
                <span className="w-1.5 h-1.5 bg-[#D4AF37] rotate-45 flex-shrink-0" />
                {t}
              </div>
            ))}
          </div>
        </section>

        {/* PROBLEM SELECTOR */}
        <ProblemSelectorSection />

        {/* FIVE SERVICES */}
        <section data-testid="home-services" className="bg-white py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="max-w-2xl mb-14">
              <Eyebrow>Three Focused Services</Eyebrow>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold text-[#0A192F] leading-[1.1]">
                One clear recommendation—<span className="text-[#1E3A8A]">not an overwhelming menu.</span>
              </h2>
              <p className="mt-6 text-sm md:text-base text-[#475569] leading-relaxed">
                AXIS does not start by selling every service. We identify the business problem,
                recommend the most appropriate starting point, and define the scope before
                production begins.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {SERVICES.map((s, i) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  data-testid={`home-service-${s.slug}`}
                  className="group bg-[#F8F9FA] border border-[#C0C0C0]/40 p-7 md:p-8 flex flex-col hover:border-[#D4AF37] transition-colors duration-300"
                >
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-3">
                    0{i + 1}
                  </div>
                  <h3 className="font-display text-xl md:text-2xl font-semibold text-[#0A192F]">
                    {s.name}
                  </h3>
                  <p className="mt-3 text-sm text-[#475569] leading-relaxed flex-1">{s.summary}</p>
                  <div className="mt-5 text-sm font-semibold text-[#1E3A8A]">{s.priceLine}</div>
                  <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0A192F] group-hover:text-[#D4AF37] transition-colors">
                    View {s.shortName} <ArrowUpRight className="w-4 h-4" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* WHY AXIS */}
        <section data-testid="home-why-axis" className="bg-[#0A192F] text-white py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <Eyebrow light>Why AXIS</Eyebrow>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold leading-[1.1]">
                Good-looking work <span className="text-[#D4AF37]">is not enough.</span>
              </h2>
              <p className="mt-6 text-sm md:text-base text-[#C0C0C0] leading-relaxed">
                AXIS combines strategic marketing, visual communication, customer-experience
                thinking, and operational judgment. The goal is not to produce more material.
                The goal is to make the business clearer, more consistent, and easier for
                customers to act on.
              </p>
            </div>
            <div className="lg:col-span-7">
              <ul className="space-y-4">
                {WHY_AXIS_POINTS.map((point) => (
                  <li
                    key={point}
                    className="flex items-start gap-3 text-sm md:text-base text-[#C0C0C0] border-b border-[#C0C0C0]/15 pb-4"
                  >
                    <span className="w-1.5 h-1.5 bg-[#D4AF37] rotate-45 mt-2 flex-shrink-0" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* HOW AXIS WORKS */}
        <section data-testid="home-process" className="bg-[#F8F9FA] py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="max-w-2xl mb-14">
              <Eyebrow>A Clear Path Forward</Eyebrow>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold text-[#0A192F] leading-[1.1]">
                From uncertainty to <span className="text-[#1E3A8A]">one recommended next step.</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
              {HOW_AXIS_WORKS.map((step) => (
                <div
                  key={step.n}
                  data-testid={`home-process-${step.n}`}
                  className="bg-white border border-[#C0C0C0]/40 p-6"
                >
                  <div className="font-display text-4xl text-[#D4AF37] font-semibold opacity-40">
                    {step.n}
                  </div>
                  <h3 className="mt-4 font-display text-lg font-semibold text-[#0A192F]">
                    {step.title}
                  </h3>
                  <p className="mt-2.5 text-sm text-[#475569] leading-relaxed">{step.blurb}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PROOF */}
        <section data-testid="home-proof" className="bg-white py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
              <div className="max-w-xl">
                <Eyebrow>Selected Work</Eyebrow>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold text-[#0A192F] leading-[1.1]">
                  Strategy made visible.
                </h2>
              </div>
              <Link
                to="/case-studies"
                data-testid="home-proof-all"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0A192F] border-b border-[#0A192F]/30 pb-1 hover:border-[#D4AF37] hover:text-[#1E3A8A] transition-colors w-fit"
              >
                View All Case Studies <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {PROOF_CARDS.map((c) => (
                <Link
                  key={c.id}
                  to="/case-studies"
                  data-testid={`home-proof-${c.id}`}
                  className="group bg-[#F8F9FA] border border-[#C0C0C0]/40 hover:border-[#D4AF37] transition-colors overflow-hidden flex flex-col"
                >
                  <div className="overflow-hidden">
                    <img
                      src={c.img}
                      alt={`${c.client} — ${c.service} case study cover`}
                      loading="lazy"
                      className="w-full h-56 object-cover object-top group-hover:scale-[1.02] transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] font-semibold">
                      {c.service}
                    </div>
                    <h3 className="mt-2 font-display text-xl font-semibold text-[#0A192F]">
                      {c.client}
                    </h3>
                    <p className="mt-2 text-sm text-[#475569] leading-relaxed flex-1">{c.problem}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FOUNDER */}
        <section data-testid="home-founder" className="bg-[#F8F9FA] py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-4">
              <img
                src="/assets/davin-campbell.jpg"
                alt="Davin Campbell, founder and strategic lead of AXIS Business Solutions"
                loading="lazy"
                className="w-full max-w-sm shadow-[0_30px_80px_-20px_rgba(0,0,0,0.3)] ring-1 ring-[#D4AF37]/20"
              />
            </div>
            <div className="lg:col-span-8">
              <Eyebrow>Founder and Strategic Lead</Eyebrow>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold text-[#0A192F] leading-[1.1]">
                Meet Davin Campbell.
              </h2>
              <p className="mt-6 text-sm md:text-base text-[#475569] leading-relaxed max-w-2xl">
                Davin brings more than 20 years of experience across marketing leadership,
                visual communications, multi-location brand rollouts, graphic design, sign
                production, customer experience, and process improvement.
              </p>
              <p className="mt-4 text-sm md:text-base text-[#475569] leading-relaxed max-w-2xl">
                He works as a consultant and advisor first—then applies design, technology, and
                production expertise to make the recommendation usable.
              </p>
              <p className="mt-6 font-display text-lg text-[#1E3A8A] font-medium">
                Technology provides insight. Experience provides judgment. AXIS combines both.
              </p>
              <div className="mt-8">
                <Link
                  to="/about"
                  data-testid="home-founder-cta"
                  className="inline-flex items-center justify-center gap-2 bg-[#0A192F] text-white px-6 py-3 text-sm font-semibold border border-[#0A192F] hover:bg-transparent hover:text-[#0A192F] transition-all duration-300"
                >
                  About AXIS <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section data-testid="home-final-cta" className="relative bg-[#0A192F] text-white py-24 md:py-32 overflow-hidden">
          <div
            className="absolute inset-0 opacity-40 pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(60% 80% at 20% 30%, rgba(30,58,138,0.5) 0%, transparent 60%), radial-gradient(50% 60% at 90% 80%, rgba(212,175,55,0.3) 0%, transparent 60%)",
            }}
            aria-hidden="true"
          />
          <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
            <div className="inline-flex items-center gap-3 mb-5 justify-center">
              <span className="h-px w-8 bg-[#D4AF37]" />
              <span className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">
                Start With Clarity
              </span>
              <span className="h-px w-8 bg-[#D4AF37]" />
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl tracking-tighter font-semibold leading-[1.0]">
              Not sure which service fits?
            </h2>
            <p className="mt-6 text-sm md:text-base text-[#C0C0C0] leading-relaxed max-w-2xl mx-auto">
              Tell AXIS what you are trying to improve. We will ask three short questions and
              recommend the most appropriate starting point.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={WA_LINKS.recommend}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-final-cta-primary"
                className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A192F] px-8 py-4 text-sm md:text-base font-semibold border border-[#D4AF37] hover:bg-transparent hover:text-[#D4AF37] transition-all duration-300"
              >
                Get My AXIS Recommendation <ArrowUpRight className="w-4 h-4" />
              </a>
              <Link
                to="/services"
                data-testid="home-final-cta-secondary"
                className="inline-flex items-center justify-center gap-2 bg-transparent text-white border border-[#C0C0C0]/40 px-7 py-3.5 text-sm font-semibold hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300"
              >
                View All Services
              </Link>
            </div>
            <p className="mt-6 text-xs text-[#94A3B8]">
              No payment required to check fit. AXIS will not send you an overwhelming package menu.
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
